import { useTranslation } from 'react-i18next';

import { APP_STORE_URL } from '../config/reference-data';

/**
 * App Store + Google Play download badges for the marketing landing page.
 *
 * - App Store: official-style black "Download on the App Store" lockup,
 *   linked to the live iOS listing (opens in a new tab, safe rel).
 * - Google Play: official-style "Get it on Google Play" lockup rendered as
 *   a dimmed, NON-clickable placeholder with a "Coming soon" ribbon — the
 *   Android app isn't published yet, so there is deliberately no href.
 *
 * Badges are self-contained inline SVG (no external image requests). The
 * lockups are sized via CSS (`.nw-store-badge` height); the SVG viewBox
 * keeps the Apple/Google glyphs proportional at any height.
 *
 * This is store-badge artwork, not the NewsWave brand mark — the locked
 * logo mark (CLAUDE.md §13) is unaffected.
 */
export type StoreBadgeVariant = 'hero' | 'footer';

interface StoreBadgesProps {
  readonly variant: StoreBadgeVariant;
}

/** Apple logo glyph, drawn white. viewBox-local path (~16-unit tall). */
function AppleGlyph() {
  return (
    <path
      fill="currentColor"
      d="M13.05 11.36c-.2.46-.44.89-.72 1.28-.38.54-.69.91-.93 1.12-.37.34-.77.52-1.2.53-.31 0-.68-.09-1.11-.27-.43-.18-.83-.27-1.19-.27-.38 0-.79.09-1.23.27-.44.18-.8.27-1.07.28-.41.02-.82-.17-1.23-.55-.26-.23-.58-.61-.97-1.16-.42-.58-.76-1.26-1.03-2.03C.79 9.05.64 8.23.64 7.44c0-.9.2-1.68.59-2.33.31-.52.72-.94 1.23-1.24.51-.3 1.07-.46 1.66-.47.33 0 .76.1 1.3.3.54.2.88.3 1.04.3.12 0 .51-.12 1.15-.35.61-.22 1.12-.31 1.54-.27 1.13.09 1.98.54 2.55 1.34-1.01.61-1.51 1.47-1.5 2.57.01.86.32 1.57.93 2.13.27.26.58.46.92.6-.07.21-.15.42-.24.61zM9.96 1.18c0 .67-.25 1.3-.73 1.88-.59.69-1.3 1.09-2.07 1.03-.01-.08-.02-.17-.02-.26 0-.65.28-1.34.78-1.91.25-.29.56-.53.95-.72.38-.19.74-.29 1.08-.31.01.1.01.2.01.29z"
    />
  );
}

/** App Store lockup — white content on a black rounded badge. */
function AppStoreBadgeSvg() {
  return (
    <svg
      className="nw-store-badge__svg"
      viewBox="0 0 120 40"
      role="presentation"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="0.5" y="0.5" width="119" height="39" rx="7" fill="#000" stroke="#A6A6A6" />
      <g transform="translate(11 12) scale(1)" color="#fff">
        <AppleGlyph />
      </g>
      <text x="32" y="16" fill="#fff" fontSize="6.5" fontFamily="system-ui, sans-serif">
        Download on the
      </text>
      <text
        x="32"
        y="29"
        fill="#fff"
        fontSize="14"
        fontWeight="600"
        fontFamily="system-ui, -apple-system, sans-serif"
      >
        App Store
      </text>
    </svg>
  );
}

/** Google Play triangle — four-colour play glyph. */
function PlayGlyph() {
  return (
    <g transform="translate(11 9.5)">
      <path d="M0 0.8v18.4c0 .55.6.9 1.07.62l2.6-1.55L0 0.8z" fill="#00D3FF" />
      <path d="M0 .8l3.67 16.67 3.5-3.5L0 .8z" fill="#00F076" />
      <path d="M0 .8l7.17 13.17 2.83-2.84L0 .8z" fill="#00F076" opacity="0" />
      <path d="M14.2 8.1L10.6 6 7.17 9.43 10.6 12.9l3.6-2.1c.6-.36.6-2.34 0-2.7z" fill="#FFC900" />
      <path d="M10.6 6L1.07.45C.6.17 0 .52 0 .8l7.17 8.63L10.6 6z" fill="#00F076" />
      <path d="M0 19.2c0 .28.6.63 1.07.35L10.6 14l-3.43-3.43L0 19.2z" fill="#FF3A44" />
    </g>
  );
}

/** Google Play lockup — white content on a black rounded badge. */
function PlayStoreBadgeSvg() {
  return (
    <svg
      className="nw-store-badge__svg"
      viewBox="0 0 120 40"
      role="presentation"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="0.5" y="0.5" width="119" height="39" rx="7" fill="#000" stroke="#A6A6A6" />
      <PlayGlyph />
      <text x="32" y="16" fill="#fff" fontSize="6" fontFamily="system-ui, sans-serif">
        GET IT ON
      </text>
      <text
        x="32"
        y="29"
        fill="#fff"
        fontSize="13"
        fontWeight="600"
        fontFamily="system-ui, -apple-system, sans-serif"
      >
        Google Play
      </text>
    </svg>
  );
}

export function StoreBadges({ variant }: StoreBadgesProps) {
  const { t } = useTranslation();
  return (
    <div className={`nw-store-badges nw-store-badges--${variant}`}>
      <a
        data-store="appstore"
        className="nw-store-badge"
        href={APP_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t('web.landing.download.appStoreAlt')}
      >
        <AppStoreBadgeSvg />
      </a>
      <span
        data-store="play"
        className="nw-store-badge nw-store-badge--soon"
        aria-disabled="true"
        aria-label={t('web.landing.download.playStoreAlt')}
      >
        <PlayStoreBadgeSvg />
        <span className="nw-store-badge__ribbon">{t('web.landing.download.comingSoon')}</span>
      </span>
    </div>
  );
}
